import React from "react";
import type { LucideIcon } from "lucide-react";

import { useUser } from "util/UserContext";
import { cn } from "util/cn";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/DropdownMenu";
import { Drawer, DrawerContent } from "ui/Drawer";

/**
 * Describes one selectable entry in a {@link MenuOrDrawer}. Keep labels
 * localized (the component never stringifies a key) and icons optional
 * for lightweight text-only menus.
 */
export interface MenuOrDrawerItem {
  /** Stable key for React reconciliation and analytics labels. */
  id: string;
  /** Human-readable label; pass already-localized strings. */
  label: string;
  /** Optional leading icon. */
  icon?: LucideIcon;
  /** Optional helper copy rendered below the label in drawer mode. */
  description?: string;
  /** Click handler; the component closes the surface automatically. */
  onSelect: () => void;
  /** Marks destructive actions with red emphasis. */
  destructive?: boolean;
  /** Disabled entries receive reduced opacity and skip click handlers. */
  disabled?: boolean;
}

export interface MenuOrDrawerProps {
  /**
   * Node rendered as the menu trigger. Wrap the actual clickable element
   * (usually a button). Must forward `onClick` or act as a Radix/Vaul
   * `asChild` child.
   */
  trigger: React.ReactNode;
  /** Controlled open state from the parent. */
  open: boolean;
  /** Controlled open/close callback. Receives `true` when opening. */
  onOpenChange: (open: boolean) => void;
  /**
   * Localized menu title. Required — we render it as the DrawerTitle on
   * mobile (accessibility) and visually hide it on desktop.
   */
  title: string;
  /** Items to render, in order. */
  items: MenuOrDrawerItem[];
  /**
   * Optional className applied to the inner content surface. Useful when
   * a caller needs extra padding (e.g. multi-section menus).
   */
  contentClassName?: string;
  /** Offset alignment for the desktop dropdown. Defaults to `"end"`. */
  align?: "start" | "center" | "end";
}

/**
 * Responsive overflow menu. On desktop it renders a Radix
 * {@link DropdownMenu}; on mobile (detected via `useUser().userIsMobile`)
 * it renders a Vaul {@link Drawer} with the same item list.
 *
 * Accessibility:
 * - `title` is announced as the drawer heading on mobile and stays
 *   visually hidden on desktop.
 * - Items render as `<button>`s in the drawer so keyboard activation
 *   works natively; on desktop they use Radix items which already
 *   handle arrow-key navigation and focus return.
 * - `onOpenChange` is called from both surfaces so parents can keep
 *   controlled state in one place.
 */
const MenuOrDrawer: React.FC<MenuOrDrawerProps> = ({
  trigger,
  open,
  onOpenChange,
  title,
  items,
  contentClassName,
  align = "end",
}) => {
  const { userIsMobile } = useUser();

  const handleSelect = (item: MenuOrDrawerItem) => {
    if (item.disabled) {
      return;
    }
    item.onSelect();
    onOpenChange(false);
  };

  if (userIsMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        {/* The trigger is rendered inline (not asChild) so the parent
            component keeps control of styling/aria attributes and can
            wire up its own onClick that toggles `open`. */}
        {trigger}
        <DrawerContent title={title}>
          <nav
            aria-label={title}
            className={cn("flex flex-col px-2 pb-4 pt-1", contentClassName)}
          >
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  disabled={item.disabled}
                  onClick={() => handleSelect(item)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-4 py-3 text-left text-sm",
                    "text-eoe-text-sub dark:text-eoe-dark-text-sub",
                    "hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eoe-primary",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    item.destructive
                      && "text-red-600 dark:text-red-400 hover:bg-red-500/10 dark:hover:bg-red-500/10"
                  )}
                >
                  {Icon ? <Icon className="size-5 shrink-0" aria-hidden="true" /> : null}
                  <span className="flex flex-col">
                    <span className="font-medium">{item.label}</span>
                    {item.description ? (
                      <span className="text-xs text-eoe-text-faint dark:text-eoe-dark-text-faint">
                        {item.description}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </nav>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={cn("min-w-[14rem] py-1", contentClassName)}
        aria-label={title}
      >
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <DropdownMenuItem
              key={item.id}
              disabled={item.disabled}
              onSelect={(event) => {
                // Radix auto-closes the menu when onSelect returns; we still
                // call onOpenChange(false) explicitly so parents with extra
                // cleanup (e.g. analytics) get the same signal.
                event.preventDefault();
                handleSelect(item);
              }}
              className={cn(
                item.destructive && "text-red-600 focus:bg-red-500/10 dark:text-red-400"
              )}
            >
              {Icon ? <Icon className="size-4 shrink-0" aria-hidden="true" /> : null}
              <span className="flex flex-col">
                <span>{item.label}</span>
                {item.description ? (
                  <span className="text-xs text-eoe-text-faint dark:text-eoe-dark-text-faint">
                    {item.description}
                  </span>
                ) : null}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MenuOrDrawer;
